import { apiRequest, getApiBase } from '../../../api/client'
import type {
  AlertsCount,
  LaneSuggestions,
  Offer,
  PlaceSuggestion,
  RouteDto,
  RouteView,
  SourcesView,
  SpotSearchCriteria,
  SpotSearchRequest,
  SpotSearchResponse,
  WatchedOffer,
} from '../model/types'
import { retryAfterSecondsOf } from './spotSearchErrors'

// Klient API Szukaj fracht v2 (spec §4, indeks §3.4). Wszystko z skipGlobalLoading (bez globalnego
// paska przy tickach i podpowiedziach); wywołania cykliczne i podpowiedzi także bez Sentry.

export interface ApiCallOptions {
  token: string | null
  signal?: AbortSignal | undefined
  /** Domyślne raportowanie błędów do Sentry dla wywołania; jawna wartość endpointu (extra) ma pierwszeństwo. */
  reportErrors?: boolean | undefined
}

export interface RouteCreateBody {
  name: string
  criteria: SpotSearchCriteria
  view: RouteView | null
  alertsEnabled: boolean
}

export interface RouteUpdateBody {
  name?: string
  criteria?: SpotSearchCriteria
  version: number
}

export interface MarksRequest {
  keys: string[]
  mark: 'HIDDEN' | 'WATCHED'
  on: boolean
  snapshot?: Partial<Offer>
  loadDate?: string
}

const BASE = '/api/spot-search'
/** Backend odpowiada najpóźniej po 8,5 s; klient czeka do 20 s (katalog bardzo-wolne-lacze). */
const SEARCH_TIMEOUT_MS = 20_000
const SUGGEST_TIMEOUT_MS = 5_000

const request = <T>(
  path: string,
  { token, signal, reportErrors }: ApiCallOptions,
  extra: { method?: string; body?: unknown; timeoutMs?: number; reportErrors?: boolean } = {}
) => {
  // exactOptionalPropertyTypes: reportErrors idzie do apiRequest tylko gdy zdefiniowane; jawna wartość endpointu wygrywa z opcją wywołującego.
  const report = extra.reportErrors ?? reportErrors
  return apiRequest<T>(`${BASE}${path}`, {
    token,
    ...(signal ? { signal } : {}),
    skipGlobalLoading: true,
    ...(extra.method ? { method: extra.method } : {}),
    ...(extra.body !== undefined ? { body: extra.body } : {}),
    ...(extra.timeoutMs !== undefined ? { timeoutMs: extra.timeoutMs } : {}),
    ...(report !== undefined ? { reportErrors: report } : {}),
  })
}

export const searchSpot = (req: SpotSearchRequest, opts: ApiCallOptions): Promise<SpotSearchResponse> =>
  request<SpotSearchResponse>('/search', opts, {
    method: 'POST',
    body: req,
    timeoutMs: SEARCH_TIMEOUT_MS,
    // Tick trybu na żywo: błędy obsługuje licznik niepowodzeń, nie Sentry (katalog live-tick-nieudany).
    ...(req.live ? { reportErrors: false } : {}),
  })

export const fetchSources = (opts: ApiCallOptions): Promise<SourcesView> =>
  request<SourcesView>('/sources', opts, { reportErrors: false })

/**
 * Podpowiedzi miejscowości i kodów. 429 limitu podpowiedzi, timeout 5 s i błąd sieci = `degraded`
 * (katalog autocomplete-limit-podpowiedz, geo-geokoder-niedostepny); przerwanie przez wywołującego
 * (nowszy wpis) propagujemy, żeby starsza odpowiedź nigdy nie nadpisała nowszej. `retryAfterSeconds` (tylko
 * przy 429) mówi, po ilu sekundach podpowiedzi mogą wrócić samoczynnie.
 */
export const suggestPlaces = async (
  query: string,
  cc: string | null,
  opts: ApiCallOptions
): Promise<{ results: PlaceSuggestion[]; degraded: boolean; retryAfterSeconds: number | null }> => {
  try {
    const response = await request<{ results?: PlaceSuggestion[]; degraded?: boolean } | null>(
      '/places/suggest',
      opts,
      { method: 'POST', body: { query, cc }, timeoutMs: SUGGEST_TIMEOUT_MS, reportErrors: false }
    )
    return {
      results: Array.isArray(response?.results) ? response.results : [],
      degraded: response?.degraded === true,
      retryAfterSeconds: null,
    }
  } catch (error) {
    if (opts.signal?.aborted) {
      throw error
    }
    return { results: [], degraded: true, retryAfterSeconds: retryAfterSecondsOf(error) }
  }
}

export const fetchRoutes = async (opts: ApiCallOptions): Promise<RouteDto[]> => {
  const routes = await request<RouteDto[] | null>('/routes', opts)
  return Array.isArray(routes) ? routes : []
}

export const createRoute = (body: RouteCreateBody, opts: ApiCallOptions): Promise<RouteDto> =>
  request<RouteDto>('/routes', opts, { method: 'POST', body })

export const updateRoute = (id: number, body: RouteUpdateBody, opts: ApiCallOptions): Promise<RouteDto> =>
  request<RouteDto>(`/routes/${id}`, opts, { method: 'PUT', body })

export const patchRouteView = (id: number, view: RouteView, opts: ApiCallOptions): Promise<RouteDto> =>
  request<RouteDto>(`/routes/${id}/view`, opts, { method: 'PATCH', body: view })

export const patchRouteAlerts = (id: number, enabled: boolean, opts: ApiCallOptions): Promise<RouteDto> =>
  request<RouteDto>(`/routes/${id}/alerts`, opts, { method: 'PATCH', body: { enabled } })

export const deleteRoute = async (id: number, opts: ApiCallOptions): Promise<RouteDto[]> => {
  const routes = await request<RouteDto[] | null>(`/routes/${id}`, opts, { method: 'DELETE' })
  return Array.isArray(routes) ? routes : []
}

/**
 * DELETE przy opuszczaniu strony (zamknięcie karty w oknie „Cofnij”): `keepalive`, którego nie ma
 * apiRequest, więc goły fetch. Wynik nieistotny — lista odświeży się przy następnym wejściu.
 */
export const deleteRouteOnExit = (id: number, token: string | null): void => {
  if (!token) {
    return
  }
  void fetch(`${getApiBase()}${BASE}/routes/${id}`, {
    method: 'DELETE',
    keepalive: true,
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => undefined)
}

export const fetchAlertsCount = (opts: ApiCallOptions): Promise<AlertsCount> =>
  request<AlertsCount>('/alerts/count', opts, { reportErrors: false })

export const setMarks = async (body: MarksRequest, opts: ApiCallOptions): Promise<void> => {
  await request<unknown>('/marks', opts, { method: 'POST', body })
}

export const fetchWatched = async (opts: ApiCallOptions): Promise<WatchedOffer[]> => {
  const watched = await request<WatchedOffer[] | null>('/marks?mark=WATCHED', opts, { reportErrors: false })
  return Array.isArray(watched) ? watched : []
}

/** Kierunki na pierwsze wejście (stan pusty). Bez Sentry: brak endpointu po cofnięciu backendu to zwykły brak podpowiedzi. */
export const fetchLaneSuggestions = async (opts: ApiCallOptions): Promise<LaneSuggestions> => {
  const response = await request<LaneSuggestions | null>('/suggestions', opts, { reportErrors: false })
  return {
    ...(response?.basis ? { basis: response.basis } : {}),
    lanes: Array.isArray(response?.lanes) ? response.lanes : [],
  }
}
